import type { NBTFormat } from 'prismarine-nbt';
import { convertBedrockBlock } from './bedrockBlocks';
import { blockColor, isAir } from './blockColor';
import { MAX_BLOCKS, StructureError, asVec3, checkBlockName, checkSize, isRecord, type UnknownRecord } from './common';
import type { PaletteEntry, StructureBlock, StructureModel, Vec3 } from './types';

/** `.mcstructure`（統合版・教育版）のルートかどうか。 */
export function isBedrockStructure(raw: unknown): boolean {
  return isRecord(raw) && isRecord(raw.structure) && 'block_indices' in raw.structure;
}

function readBlockPalette(structure: UnknownRecord): PaletteEntry[] {
  const palette = isRecord(structure.palette) ? structure.palette : undefined;
  const defaultPalette = palette && isRecord(palette.default) ? palette.default : undefined;
  const entries = defaultPalette?.block_palette;
  if (!Array.isArray(entries) || entries.length === 0) {
    throw new StructureError('palette（ブロック種類）が見つかりません。構造ブロックで書き出した.mcstructureか確認してください。');
  }

  return entries.map((value, index) => {
    if (!isRecord(value)) throw new StructureError(`palette[${index}]の形式が不正です。`);
    const rawName = checkBlockName(value.name ?? value.Name, `palette[${index}]のname`);

    const { name, properties } = convertBedrockBlock(rawName, value.states);
    const propertyLabel = Object.entries(properties)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => `${key}=${item}`)
      .join(', ');
    return {
      name,
      properties,
      label: propertyLabel ? `${name} [${propertyLabel}]` : name,
      color: blockColor(name),
    };
  });
}

/**
 * `.mcstructure`を共通の`StructureModel`へ変換する。
 * ブロックはpaletteへの添字を`z`が最も速く進む順に並べたもので、-1は空白を表す。
 * 水などの第2レイヤーは表示に使わない。
 */
export function normalizeBedrockStructure(raw: unknown, format: NBTFormat, name = 'structure.mcstructure'): StructureModel {
  if (!isRecord(raw)) throw new StructureError('NBTのルートがCompoundではありません。');
  if (!isRecord(raw.structure)) throw new StructureError('structureが見つかりません。');

  const size = asVec3(raw.size, 'size');
  checkSize(size);
  const [sizeX, sizeY, sizeZ] = size;
  // セル数の上限はcheckSizeが見ている。
  const volume = sizeX * sizeY * sizeZ;

  const layers = raw.structure.block_indices;
  if (!Array.isArray(layers) || !Array.isArray(layers[0])) {
    throw new StructureError('block_indices（配置ブロック）が見つかりません。');
  }
  const indices = layers[0] as unknown[];
  if (indices.length !== volume) {
    throw new StructureError(`block_indicesの個数（${indices.length}）がsize（${volume}）と一致しません。`);
  }

  const palette = readBlockPalette(raw.structure);
  const blocks: StructureBlock[] = [];
  let placed = 0;

  for (let index = 0; index < indices.length; index += 1) {
    const state = Number(indices[index]);
    if (!Number.isInteger(state) || state < 0) continue;
    if (state >= palette.length) {
      throw new StructureError(`block_indices[${index}]がpaletteの範囲外です。`);
    }
    placed += 1;
    if (isAir(palette[state].name)) continue;
    if (blocks.length >= MAX_BLOCKS) {
      throw new StructureError(`ブロック数が上限${MAX_BLOCKS.toLocaleString()}個を超えています。`);
    }

    const position: Vec3 = [
      Math.floor(index / (sizeZ * sizeY)),
      Math.floor(index / sizeZ) % sizeY,
      index % sizeZ,
    ];
    blocks.push({ position, state });
  }

  if (blocks.length === 0) throw new StructureError('表示できるブロックがありません。');
  return { name, format, size, palette, blocks, sourceBlockCount: placed };
}
